import { RISK_RANK } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/common/riskRank'
import {
  THRESHOLD_NEIGHBORING_REINFORCED,
  THRESHOLD_NEIGHBORING_STEEL
} from '@/constants/accessor/api/emergencyInspectionResult/emergencyAssessment/resultStatus'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { GlobalError } from '@/utils/globalError/GlobalError'
import type { OverallResult } from '@/types/accessor/api/emergencyInspectionResult/emergencyAssessment/overallResult'

// 計算処理ではnumber型で扱うので変換する
type EachResultConst = -1 | 1 | 2 | 3
const EachResultConst = {
  notSelected: Number(RISK_RANK.UNSELECTED),
  researched: Number(RISK_RANK.RANK_A),
  caution: Number(RISK_RANK.RANK_B),
  danger: Number(RISK_RANK.RANK_C)
} as const

/**
 * stringをnumberに変換
 * @param value 文字列
 * @returns 数値
 */
export const convertStringToNumber = (value: string): number => {
  const convertNumber = Number(value)
  if (isNaN(convertNumber)) {
    throw new GlobalError([ERROR_CODES.STEP2_STRING_CONVERSION_FAILED])
  }
  return convertNumber
}

// --- step1 ---
/**
 * step1の調査結果を判定する
 * @param inputValue step1入力値
 * @returns step1判定値
 */
export const calculationStep1Result = (inputValue: number): EachResultConst => {
  if (inputValue >= EachResultConst.researched)
    return EachResultConst.danger as unknown as EachResultConst
  return inputValue === EachResultConst.notSelected
    ? (EachResultConst.notSelected as unknown as EachResultConst)
    : (EachResultConst.researched as unknown as EachResultConst)
}

// --- step2 ---
/**
 * 木造用
 *「隣接建築物・周辺地盤等及び構造躯体」の調査結果を判定する
 * @param inputList「隣接建築物・周辺地盤等及び構造躯体」入力値
 * @returns「隣接建築物・周辺地盤等及び構造躯体」判定値
 */
export const calculationNeighboringWood = (inputList: number[]): EachResultConst => {
  if (inputList.every(item => item === EachResultConst.notSelected))
    return EachResultConst.notSelected as unknown as EachResultConst
  if (inputList.includes(EachResultConst.danger))
    return EachResultConst.danger as unknown as EachResultConst
  if (inputList.includes(EachResultConst.caution))
    return EachResultConst.caution as unknown as EachResultConst
  return EachResultConst.researched as unknown as EachResultConst
}

/**
 * 木造用
 * 「落下危険物・転倒危険物に関する危険度」の調査結果を判定する
 * @param inputList 「落下危険物・転倒危険物に関する危険度」入力値
 * @returns 「落下危険物・転倒危険物に関する危険度」判定値
 */
export const calculationFallingWood = (inputList: number[]): EachResultConst => {
  if (inputList.every(item => item === EachResultConst.notSelected))
    return EachResultConst.notSelected as unknown as EachResultConst
  if (inputList.includes(EachResultConst.danger))
    return EachResultConst.danger as unknown as EachResultConst
  if (inputList.includes(EachResultConst.caution))
    return EachResultConst.caution as unknown as EachResultConst
  return EachResultConst.researched as unknown as EachResultConst
}

/**
 * 鉄骨造用
 * 「隣接建築物・周辺地盤等及び構造躯体」の調査結果を判定する
 * @param inputList 「隣接建築物・周辺地盤等及び構造躯体」入力値
 * @returns 判定値
 */
export const calculationNeighboringSteel = (inputList: number[]): EachResultConst => {
  if (inputList.every(item => item === EachResultConst.notSelected))
    return EachResultConst.notSelected as unknown as EachResultConst
  if (
    inputList.includes(EachResultConst.danger) ||
    inputList.filter(item => item === EachResultConst.caution).length >=
      THRESHOLD_NEIGHBORING_STEEL.THRESHOLD_RANK_B
  )
    return EachResultConst.danger as unknown as EachResultConst
  if (
    inputList.includes(EachResultConst.caution) &&
    inputList.filter(item => item === EachResultConst.caution).length <
      THRESHOLD_NEIGHBORING_STEEL.THRESHOLD_RANK_B
  )
    return EachResultConst.caution as unknown as EachResultConst
  return EachResultConst.researched as unknown as EachResultConst
}

/**
 * 鉄骨造用
 * 「落下危険物・転倒危険物に関する危険度」の調査結果を判定する
 * @param inputList 「落下危険物・転倒危険物に関する危険度」入力値
 * @returns 「落下危険物・転倒危険物に関する危険度」判定値
 */
export const calculationFallingSteel = (inputList: number[]): EachResultConst => {
  if (inputList.every(item => item === EachResultConst.notSelected))
    return EachResultConst.notSelected as unknown as EachResultConst
  if (inputList.includes(EachResultConst.danger))
    return EachResultConst.danger as unknown as EachResultConst
  if (inputList.includes(EachResultConst.caution))
    return EachResultConst.caution as unknown as EachResultConst
  return EachResultConst.researched as unknown as EachResultConst
}

/**
 * 鉄筋・鉄筋コンクリート造用
 * 「隣接建築物・周辺地盤等及び構造躯体」の調査結果を判定する
 * @param inputFirstData 「隣接建築物・周辺地盤等及び構造躯体」(判定1)入力値
 * @param inputSecondList 「隣接建築物・周辺地盤等及び構造躯体」(判定2)入力値
 * @returns 「隣接建築物・周辺地盤等及び構造躯体」判定値
 */
export const calculationNeighboringReinforced = (
  inputFirstData: number,
  inputSecondList: number[]
): EachResultConst => {
  if ([inputFirstData, ...inputSecondList].every(item => item === EachResultConst.notSelected))
    return EachResultConst.notSelected as unknown as EachResultConst
  const section1 = inputFirstData
  const section2 = (() => {
    if (
      inputSecondList.includes(EachResultConst.danger) ||
      inputSecondList.filter(item => item === EachResultConst.caution).length >=
        THRESHOLD_NEIGHBORING_REINFORCED.THRESHOLD_RANK_B
    )
      return EachResultConst.danger
    if (inputSecondList.includes(EachResultConst.caution)) return EachResultConst.caution
    return EachResultConst.researched
  })()

  return Math.max(section1, section2) as unknown as EachResultConst
}

/**
 * 鉄筋・鉄筋コンクリート造用
 * 「落下危険物・転倒危険物に関する危険度」の調査結果を判定する
 * @param inputList 「落下危険物・転倒危険物に関する危険度」入力値
 * @returns 「落下危険物・転倒危険物に関する危険度」判定値
 */
export const calculationFallingReinforced = (inputList: number[]): EachResultConst => {
  if (inputList.every(item => item === EachResultConst.notSelected))
    return EachResultConst.notSelected as unknown as EachResultConst
  if (inputList.includes(EachResultConst.danger))
    return EachResultConst.danger as unknown as EachResultConst
  if (inputList.includes(EachResultConst.caution))
    return EachResultConst.caution as unknown as EachResultConst
  return EachResultConst.researched as unknown as EachResultConst
}

// --- 判定結果 ---
/**
 * 判定結果算出
 * @param firstStepValue step1判定値
 * @param NeighboringValue 「隣接建築物・周辺地盤等及び構造躯体」判定値
 * @param FallingValue 「落下危険物・転倒危険物に関する危険度」判定値
 * @returns 総合判定値
 * @note step2（NeighboringValue, FallingValue）が両方未選択の場合はstep1の判定値を使用する
 * step2のいずれかが選択されている場合は、step2の高い方の判定値を使用する
 */
export const determineOverallAssessmentResult = (
  firstStepValue: number,
  NeighboringValue: number,
  FallingValue: number
): OverallResult => {
  const isAllStepsUnselectedError = [firstStepValue, NeighboringValue, FallingValue].every(
    item => item === EachResultConst.notSelected
  )
  if (isAllStepsUnselectedError) throw new GlobalError([ERROR_CODES.STEP2_ALL_UNSELECTED])
  const step2BothUnselected =
    NeighboringValue === EachResultConst.notSelected && FallingValue === EachResultConst.notSelected
  // step2（NeighboringValue, FallingValue）が両方未選択の場合のみ、step1の判定値を使用する
  return String(
    step2BothUnselected ? firstStepValue : Math.max(NeighboringValue, FallingValue)
  ) as OverallResult
}
